import { useAnimateOnScroll } from "@/hooks/useAnimateOnScroll";
import { useScrollProgress } from "@/hooks/useScrollProgress";
import { SpotlightIconPattern } from "./SpotlightIconPattern";

interface SpotlightAgent {
  name: string;
  headshot_url: string | null;
}

interface SpotlightTeamProps {
  agents: SpotlightAgent[];
  teamName: string;
  primaryColor?: string | null;
}

function getGridCols(count: number): string {
  if (count === 1) return "grid-cols-1 max-w-xs mx-auto";
  if (count === 2) return "grid-cols-2 max-w-lg mx-auto";
  if (count === 3) return "grid-cols-2 sm:grid-cols-3 max-w-2xl mx-auto";
  if (count === 4) return "grid-cols-2 md:grid-cols-4 max-w-4xl mx-auto";
  return "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5";
}

export function SpotlightTeam({ agents, teamName, primaryColor }: SpotlightTeamProps) {
  const { ref: animRef, isVisible } = useAnimateOnScroll(0.15);
  const { ref: scrollRef, progress } = useScrollProgress();

  if (agents.length === 0) return null;

  const parallaxY = (progress - 0.5) * -20;

  return (
    <section ref={scrollRef} className="relative w-full py-24 md:py-32 px-6" style={{ background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f8fafc 100%)" }}>
      <SpotlightIconPattern />
      <div
        ref={animRef}
        className="max-w-7xl mx-auto will-change-transform"
        style={{ transform: `translateY(${parallaxY}px)` }}
      >
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400 mb-12">
          Meet the Team
        </p>

        <div className={`grid gap-8 ${getGridCols(agents.length)}`}>
          {agents.map((agent, index) => (
            <div
              key={index}
              className={`group transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-8 scale-95"
              }`}
              style={{ transitionDelay: `${index * 120}ms` }}
            >
              <div className="relative w-36 h-36 sm:w-44 sm:h-44 mx-auto rounded-full overflow-hidden shadow-lg group-hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-1">
                {agent.headshot_url ? (
                  <img
                    src={agent.headshot_url}
                    alt={agent.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center text-white text-4xl font-bold"
                    style={{ backgroundColor: primaryColor ?? "#3B82F6" }}
                  >
                    {agent.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>
                )}
              </div>
              <p className="text-lg font-semibold text-slate-900 mt-4 text-center">{agent.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
